import { Delete, Edit, ExpandMore } from '@mui/icons-material';
// Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  FormLabel,
  Button
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
import { IFieldWalkthrough } from '../../../models';
import { Link } from 'react-router-dom';
import { formatDate } from '../../../helpers';

interface IMobileFieldWalkthroughProps {
  original: IFieldWalkthrough;
  handleDelete: (val: IFieldWalkthrough) => void;
  isDeletable: (val: IFieldWalkthrough) => boolean;
}

export const MobileFieldWalkthroughs: FC<IMobileFieldWalkthroughProps> = ({
  original,
  handleDelete,
  isDeletable
}) => {
  const classes = useStyles();
  const {
    fieldWalkthroughId,
    neighborhoodDescription,
    lotNumber,
    planDescription,
    createdOn,
    createdBy,
    walkthroughStatus
  } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography color="primary" className={classes.boldName}>
            <span className={clsx(classes.boldName, classes.subLabel)}>
              {neighborhoodDescription} - Lot {lotNumber}
            </span>
          </Typography>
          <div className={classes.buttonsWrapper}>
            <IconButton
              className={classes.editButton}
              color="primary"
              component={Link}
              to={`/production/field-walkthroughs/${fieldWalkthroughId}`}
              title="Details"
              size="large"
            >
              <Edit />
            </IconButton>
            {isDeletable(original) && (
              <IconButton
                className={classes.deleteButton}
                color="primary"
                component={Button}
                onClick={() => handleDelete(original)}
                title="Details"
                size="large"
              >
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Neighborhood Description:
          </FormLabel>
          <Typography component="span">{neighborhoodDescription || '--'}</Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Lot Number:
          </FormLabel>
          <Typography component="span">{lotNumber || '--'}</Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Plan Description:
          </FormLabel>
          <Typography component="span">{planDescription || '--'}</Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Created On:
          </FormLabel>
          <Typography component="span">{formatDate(createdOn) || '--'}</Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Created By:
          </FormLabel>
          <Typography component="span">{createdBy || '--'}</Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span">{walkthroughStatus || '--'}</Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    marginBottom: '0.5rem',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10
  },
  buttonsWrapper: {
    display: 'flex'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    color: theme.palette.error.main
  }
}));
